import { useState, useEffect, useMemo } from 'react'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { accountsService, PropProvider, ProviderAccess, CalculatorResponse } from '@/services/accounts.service'
import ixenSystemService, { IxenPair } from '@/services/ixen_system.service'
import { toast } from 'sonner'
import { SubscriptionBlocker } from '@/components/SubscriptionBlocker'
import { useAuthStore } from '@/stores/auth.store'
import { ProviderPurchaseModal } from '@/components/ProviderPurchaseModal'

// ─── Constants (mirrored from TradingCalculator.tsx + lot_manager.py) ───────

const COVERAGE_MAPPING: Record<string, number> = {
  CHALLENGE: 0.25,
  REAL: 0.70,
  REAL_PRE_PAYOUT: 0.70,
  REAL_POST_PAYOUT: 0.60,
}

const TP_MAX_PCT: Record<string, Record<string, number>> = {
  FundedNext:  { CHALLENGE: 0.06, REAL_PRE_PAYOUT: 0.02, REAL_POST_PAYOUT: 0.02 },
  FundingPips: { CHALLENGE: 0.04, REAL: 0.035 },
  FunderPro:   { CHALLENGE: 0.04, REAL_PRE_PAYOUT: 0.02, REAL_POST_PAYOUT: 0.02 },
  'FundingPips 2 Step': { ONE_STEP: 0.012, TWO_STEP: 0.012, MASTER: 0.012 },
}

// ─── FP2 (FundingPips 2 Step) — mirror di app/services/fp2.py ─────────────
const FP2_PROVIDER_NAME = 'FundingPips 2 Step'
const FP2_COVERAGE: Record<string, Record<string, number>> = {
  ONE_STEP: { conservativo: 0.17, aggressivo: 0.20 },
  TWO_STEP: { conservativo: 0.34, aggressivo: 0.40 },
  MASTER:   { conservativo: 0.70, aggressivo: 0.70 },
}
// (lotti_del_tier, leva) — ultimo tier illimitato
const FP2_MASTER_TIERS: [number | null, number][] = [
  [0.05, 50], [0.05, 30], [0.05, 25], [0.10, 20], [0.25, 10], [null, 5],
]

function fp2MasterLotsFromMargin(targetMargin: number, goldPrice: number): number {
  if (targetMargin <= 0 || goldPrice <= 0) return 0.01
  const notionalPerLot = goldPrice * 100
  let remaining = targetMargin
  let lots = 0
  for (const [tierLots, lev] of FP2_MASTER_TIERS) {
    const marginPerLot = notionalPerLot / lev
    if (tierLots === null) { lots += remaining / marginPerLot; break }
    const tierMargin = tierLots * marginPerLot
    if (remaining >= tierMargin) { lots += tierLots; remaining -= tierMargin }
    else { lots += remaining / marginPerLot; remaining = 0; break }
  }
  return Math.max(0.01, Math.floor(lots * 100) / 100)
}

const PROP_SIZE_RANGES_DEFAULT: Record<string, { min: number; max: number }> = {
  '50000':  { min: 47000, max: 55000 },
  '100000': { min: 94000, max: 110000 },
  '200000': { min: 188000, max: 220000 },
}

const PROP_SIZE_RANGES_FUNDERPRO: Record<string, { min: number; max: number }> = {
  '50000':  { min: 45000, max: 55000 },
  '100000': { min: 90000, max: 110000 },
  '150000': { min: 135000, max: 165000 },
  '200000': { min: 180000, max: 220000 },
}

const PHASE_LABELS: Record<string, string> = {
  CHALLENGE:       'Challenge',
  REAL:            'Real',
  REAL_PRE_PAYOUT: 'Pre 3° Payout',
  REAL_POST_PAYOUT:'Post 3° Payout',
  ONE_STEP:        'One Step',
  TWO_STEP:        'Two Step',
  MASTER:          'Master',
}

const FUNDERPRO_PHASE_LABELS: Record<string, string> = {
  CHALLENGE:       'Challenge',
  REAL_PRE_PAYOUT: 'Pre 1° Payout',
  REAL_POST_PAYOUT:'Post 1° Payout',
}

// ─── Lot calculation (identical to TradingCalculator.tsx) ─────────────────

function calculateDynamicLots(
  propSize: number,
  goldPrice: number,
  phase: string,
  provider: string,
  funderProMarginPercent?: number,
  fundedNextLeverage?: 10 | 15,
  fp2RiskProfile?: 'conservativo' | 'aggressivo',
): { lotProp: number; lotBroker: number; coveragePercent: number } {
  const CONTRACT_SIZE = 100

  if (provider === FP2_PROVIDER_NAME) {
    // Budget margine identico agli altri provider; lotti sempre arrotondati in difetto
    const targetMargin = propSize * 0.94 * 0.70 * 0.965
    let lotProp: number
    if (phase === 'MASTER') {
      lotProp = fp2MasterLotsFromMargin(targetMargin, goldPrice)
    } else {
      const marginPerLot = (goldPrice * CONTRACT_SIZE) / 10 // one/two step: leva statica 1:10
      lotProp = Math.max(0.01, Math.floor((targetMargin / marginPerLot) * 100) / 100)
    }
    const coverageDecimal = FP2_COVERAGE[phase]?.[fp2RiskProfile ?? 'conservativo'] ?? 0.17
    const lotBroker = Math.max(0.01, Math.floor(lotProp * coverageDecimal * 100) / 100)
    return { lotProp, lotBroker, coveragePercent: Math.round(coverageDecimal * 100) }
  }

  if (provider === 'FunderPro') {
    const LEVERAGE = 10
    const isChallenge = phase === 'CHALLENGE'
    // Challenge: usa l'override se fornito (Prudente/Standard/Massimale), altrimenti 95%
    // Fasi Real: margine fisso al 20%, non modificabile
    const marginPercent = isChallenge ? (funderProMarginPercent ?? 0.95) : 0.20
    const coverageMap: Record<string, number> = {
      CHALLENGE: 0.25,
      REAL_PRE_PAYOUT: 0.70,
      REAL_POST_PAYOUT: 0.60,
    }
    const coverageDecimal = coverageMap[phase] ?? 0.25
    const marginPerLot = (goldPrice * CONTRACT_SIZE) / LEVERAGE
    const lotProp = Math.round((propSize * marginPercent / marginPerLot) * 0.95 * 100) / 100  // 5% buffer per fluttuazioni/arrotondamenti broker
    const lotBroker = Math.round(lotProp * coverageDecimal * 100) / 100
    return { lotProp, lotBroker, coveragePercent: Math.round(coverageDecimal * 100) }
  }

  // FundedNext + FundingPips: pure dynamic sizing (no base_lot, no clamp)
  // FundedNext leverage (May 2026): Challenge 1:10 o 1:15 (scelta utente), Real (pre/post) 1:10 sempre.
  // FundingPips leverage: Challenge 1:10, Real 1:5
  const isFundingPips = provider === 'FundingPips'
  const isRealPhase = phase === 'REAL' || phase === 'REAL_PRE_PAYOUT' || phase === 'REAL_POST_PAYOUT'
  const fndChallengeLev: 10 | 15 = (fundedNextLeverage === 10 || fundedNextLeverage === 15) ? fundedNextLeverage : 15
  const LEVERAGE = isFundingPips
    ? (isRealPhase ? 5 : 10)
    : (isRealPhase ? 10 : fndChallengeLev)

  const coverageDecimal = COVERAGE_MAPPING[phase] ?? 0.25

  const minEquity = propSize * 0.94
  const maxMargin = minEquity * 0.70
  const targetMargin = maxMargin * 0.965
  const marginPerLot = (goldPrice * CONTRACT_SIZE) / LEVERAGE
  const lotDynamic = targetMargin / marginPerLot

  const lotProp = Math.round(lotDynamic * 100) / 100
  const lotBroker = Math.round(lotProp * coverageDecimal * 100) / 100
  return { lotProp, lotBroker, coveragePercent: Math.round(coverageDecimal * 100) }
}

function deducePropSize(equity: number, provider: string): number {
  const sizes =
    provider === 'FunderPro'
      ? [
          { size: 50000,  min: 45000,  max: 55000  },
          { size: 100000, min: 90000,  max: 110000 },
          { size: 150000, min: 135000, max: 165000 },
          { size: 200000, min: 180000, max: 220000 },
        ]
      : [
          { size: 50000,  min: 45000,  max: 55000  },
          { size: 100000, min: 90000,  max: 110000 },
          { size: 200000, min: 180000, max: 220000 },
        ]
  for (const { size, min, max } of sizes)
    if (equity >= min && equity <= max) return size
  return sizes.reduce((c, x) =>
    Math.abs(equity - x.size) < Math.abs(equity - c.size) ? x : c
  ).size
}

// ─── Styling helpers ──────────────────────────────────────────────────────

const card = 'bg-[#0d1529] border border-[#1e3a5f] rounded-xl p-4 mb-4'
const cardTitle = 'text-[11px] font-bold tracking-widest uppercase text-[#64748b] mb-3 flex items-center gap-2 before:content-["•"] before:text-[#38bdf8] before:text-base'
const fmtUsd = (n: number) =>
  '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtDelta = (n: number) =>
  (n >= 0 ? '+' : '-') + '$' + Math.abs(Math.round(n)).toLocaleString('en-US')

// ─── Component ────────────────────────────────────────────────────────────

export const IxenConsole = () => {
  const { isAdmin } = useAuthStore()
  const hasActiveSubscription = useAuthStore(s => s.hasActiveSubscription)
  const isRestrictedUser = useAuthStore(s => s.user?.emergency_mode ?? false)
  const canAccess = isAdmin || hasActiveSubscription

  // ── Context inputs
  const [providers, setProviders] = useState<PropProvider[]>([])
  const [ixenPairs, setIxenPairs] = useState<IxenPair[]>([])
  const [providerAccessList, setProviderAccessList] = useState<ProviderAccess[]>([])
  const [selectedProvider, setSelectedProvider] = useState('')
  const [selectedPropSize, setSelectedPropSize] = useState('')
  const [propEquity, setPropEquity] = useState('')
  const [selectedPairId, setSelectedPairId] = useState('')
  const [selectedPhase, setSelectedPhase] = useState('')

  // ── FunderPro margin mode (solo per FunderPro Challenge)
  const [funderProMarginMode, setFunderProMarginMode] = useState<'prudente' | 'standard' | 'massimale'>('massimale')
  const FUNDERPRO_MARGIN_MAP = { prudente: 0.65, standard: 0.80, massimale: 0.95 } as const
  const funderProMarginDecimal = FUNDERPRO_MARGIN_MAP[funderProMarginMode]

  // ── FundedNext leverage (solo per FundedNext Challenge) — default 15 (massimale)
  const [fundedNextLeverage, setFundedNextLeverage] = useState<10 | 15>(15)

  // ── FP2 profilo rischio (solo FundingPips 2 Step)
  const [fp2RiskProfile, setFp2RiskProfile] = useState<'conservativo' | 'aggressivo'>('conservativo')
  const isFp2 = selectedProvider === FP2_PROVIDER_NAME
  // Profilo della coppia selezionata (immutabile lato server): se presente, vince e blocca il selettore
  const selectedPairFp2Profile = useMemo(() => {
    if (!isFp2 || !selectedPairId) return null
    const p = ixenPairs.find(x => String(x.id) === selectedPairId)
    const prof = (p as any)?.fp2_risk_profile
    return prof === 'conservativo' || prof === 'aggressivo' ? prof as 'conservativo' | 'aggressivo' : null
  }, [isFp2, selectedPairId, ixenPairs])
  const selectedPairControlMode = useMemo(() => {
    if (!selectedPairId) return false
    return !!ixenPairs.find(x => String(x.id) === selectedPairId)?.control_mode
  }, [selectedPairId, ixenPairs])
  useEffect(() => {
    if (selectedPairFp2Profile) setFp2RiskProfile(selectedPairFp2Profile)
  }, [selectedPairFp2Profile])
  const fp2ProfileLocked = selectedPairFp2Profile !== null

  // ── Trade inputs
  const [direction, setDirection] = useState<'BUY' | 'SELL' | ''>('')
  const [entryPrice, setEntryPrice] = useState('')

  // ── SL slider state
  const [slPercent, setSlPercent] = useState(0.01)

  // ── TP slider state (percentage of prop size)
  const [tpPercent, setTpPercent] = useState(0.02)

  // ── Spread
  const [useDynamicSpread, setUseDynamicSpread] = useState(false)
  const [propSpreadPoints, setPropSpreadPoints] = useState('')
  const [brokerSpreadPoints, setBrokerSpreadPoints] = useState<number | null>(null)
  const [brokerPointSize, setBrokerPointSize] = useState<number | null>(null)
  const [loadingBrokerSpread, setLoadingBrokerSpread] = useState(false)
  const [brokerSpreadError, setBrokerSpreadError] = useState<string | null>(null)

  // ── Market data
  const [goldPrice, setGoldPrice] = useState<number | null>(null)
  const [brokerEquity, setBrokerEquity] = useState<number | null>(null)
  const [brokerMarginFree, setBrokerMarginFree] = useState<number | null>(null)
  const [loadingGoldPrice, setLoadingGoldPrice] = useState(false)
  // ── EA status (Fase B: dati broker da telemetria EA invece che da MetaAPI)
  const [eaStatus, setEaStatus] = useState<'live' | 'stale' | 'offline' | null>(null)
  const [eaAgeSec, setEaAgeSec] = useState<number | null>(null)
  const [eaErrorMsg, setEaErrorMsg] = useState<string | null>(null)

  // ── Provider purchase modal
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false)
  const [selectedLockedProvider, setSelectedLockedProvider] = useState<ProviderAccess | null>(null)

  // ─── Derived: prop size ranges validation
  const propSizeRanges = selectedProvider === 'FunderPro' ? PROP_SIZE_RANGES_FUNDERPRO : PROP_SIZE_RANGES_DEFAULT
  const equityValidation = useMemo(() => {
    if (!selectedPropSize || !propEquity) return { isValid: true, message: '' }
    const eq = parseFloat(propEquity)
    if (isNaN(eq)) return { isValid: false, message: 'Inserisci un valore numerico' }
    const range = propSizeRanges[selectedPropSize]
    if (!range) return { isValid: true, message: '' }
    if (eq < range.min || eq > range.max)
      return { isValid: false, message: `Equity deve essere tra $${range.min.toLocaleString()} e $${range.max.toLocaleString()}` }
    return { isValid: true, message: '' }
  }, [selectedPropSize, propEquity, propSizeRanges])

  // ─── SL limits
  const slLimits = useMemo(() => {
    const providerMax = 0.012
    const MIN_SL = 0.002
    if (!propEquity || !selectedPropSize) return { maxSlPercent: providerMax, bufferDollar: 0, bufferPercent: providerMax, providerMax }
    const eq = parseFloat(propEquity)
    const ps = parseFloat(selectedPropSize)
    if (isNaN(eq) || isNaN(ps) || ps <= 0) return { maxSlPercent: providerMax, bufferDollar: 0, bufferPercent: providerMax, providerMax }
    const minEquity = ps * 0.94
    const bufferDollar = Math.max(0, eq - minEquity)
    const bufferPercent = bufferDollar / ps
    const maxSlPercent = Math.max(MIN_SL, Math.min(providerMax, bufferPercent))
    return { maxSlPercent, bufferDollar, bufferPercent, providerMax }
  }, [propEquity, selectedPropSize])

  // ─── TP max effettivo: min(cap provider/fase, residuo reale verso il target)
  // Si aggiorna live ad ogni cambio di equity, provider, fase
  const tpEffectiveMax = useMemo(() => {
    const isFndRealControl = selectedPairControlMode
      && selectedProvider === 'FundedNext'
      && ['REAL', 'REAL_PRE_PAYOUT', 'REAL_POST_PAYOUT'].includes(selectedPhase)
    const providerMax = isFndRealControl
      ? 0.03
      : (TP_MAX_PCT[selectedProvider]?.[selectedPhase] ?? 0.04)

    const equity = parseFloat(propEquity)
    const propSize = parseFloat(selectedPropSize)
    if (!selectedProvider || !selectedPhase || isNaN(equity) || equity <= 0 || isNaN(propSize) || propSize <= 0) {
      return providerMax
    }

    // Target equity specifico per provider/fase
    let targetEquity: number
    if (selectedProvider === FP2_PROVIDER_NAME) {
      // FP2: one/two step +6%, master +2%
      targetEquity = selectedPhase === 'MASTER' ? propSize * 1.02 : propSize * 1.06
    } else if (selectedPhase === 'CHALLENGE') {
      // Tutti i provider: target +10%
      targetEquity = propSize * 1.10
    } else if (selectedProvider === 'FundingPips' && selectedPhase === 'REAL') {
      targetEquity = propSize * 1.035
    } else if (selectedProvider === 'FundedNext') {
      // REAL_PRE_PAYOUT / REAL_POST_PAYOUT
      targetEquity = propSize * (isFndRealControl ? 1.03 : 1.02)
    } else {
      // FunderPro REAL_PRE_PAYOUT / REAL_POST_PAYOUT
      // Allineato con backend _compute_payout_target_equity (Q1 utente: 1.02, non 1.035)
      targetEquity = propSize * 1.02
    }

    const remainingDollar = Math.max(0, targetEquity - equity)
    const remainingPercent = remainingDollar / propSize  // in decimale es. 0.015

    // Mai sopra il cap del provider, mai sotto il minimo assoluto dello slider (0.01%)
    return Math.max(0.0001, Math.min(providerMax, remainingPercent))
  }, [selectedProvider, selectedPhase, propEquity, selectedPropSize, selectedPairControlMode])

  // Effective SL: clamp silently for display+calc, never force-reset the user's preference
  const effectiveSlPercent = Math.min(slPercent, slLimits.maxSlPercent)

  // ─── Aggiorna tpPercent quando cambiano i limiti (provider, fase, equity)
  useEffect(() => {
    setTpPercent(tpEffectiveMax)
  }, [tpEffectiveMax])

  // ─── Lots + hedge status (available as soon as equity + phase + broker are set)
  const lotsCalc = useMemo(() => {
    const equity = parseFloat(propEquity)
    if (!selectedProvider || !selectedPhase || isNaN(equity) || equity <= 0 || !goldPrice) return null

    const propSize = deducePropSize(equity, selectedProvider)
    const { lotProp, lotBroker, coveragePercent } = calculateDynamicLots(
      propSize, goldPrice, selectedPhase, selectedProvider,
      selectedProvider === 'FunderPro' ? funderProMarginDecimal : undefined,
      selectedProvider === 'FundedNext' ? fundedNextLeverage : undefined,
      selectedProvider === FP2_PROVIDER_NAME ? fp2RiskProfile : undefined,
    )

    const coverageDecimal = coveragePercent / 100
    const BROKER_LEVERAGE = 500
    // TMGM: stop out 20% + buffer di sicurezza 20% = 0.40 (era 0.60 con stop out 40%)
    const BROKER_STOP_OUT_BUFFER = 0.40
    const brokerMarginOpening = (goldPrice * lotBroker * 100) / BROKER_LEVERAGE

    // Estimate hedge status using current tpPercent
    const tpDollar = propSize * tpPercent
    const brokerTpCoverage = tpDollar * coverageDecimal
    const brokerMinRequired = brokerTpCoverage + brokerMarginOpening * BROKER_STOP_OUT_BUFFER + 200
    const brokerCanTrade = brokerEquity !== null && brokerEquity >= brokerMinRequired

    return { propSize, lotProp, lotBroker, coveragePercent, brokerMarginOpening, brokerMinRequired, brokerEquity, brokerCanTrade }
  }, [propEquity, selectedProvider, selectedPhase, goldPrice, tpPercent, brokerEquity, funderProMarginDecimal, fundedNextLeverage, fp2RiskProfile])

  // ─── API result state (mirrors TradingCalculator)
  const [calcResult, setCalcResult] = useState<CalculatorResponse | null>(null)
  const [calcLoading, setCalcLoading] = useState(false)

  // ─── Trigger API call with debounce whenever all required fields are ready
  useEffect(() => {
    const entry = parseFloat(entryPrice)
    const equity = parseFloat(propEquity)
    const phaseMapping: Record<string, string> = {
      CHALLENGE: 'challenge',
      REAL: 'real',
      REAL_PRE_PAYOUT: 'real_pre_payout',
      REAL_POST_PAYOUT: 'real_post_payout',
      ONE_STEP: 'one_step',
      TWO_STEP: 'two_step',
      MASTER: 'master',
    }
    const mappedPhase = phaseMapping[selectedPhase]

    if (!selectedProvider || !mappedPhase || !direction || isNaN(entry) || entry <= 0 || isNaN(equity) || equity <= 0 || !selectedPairId) {
      setCalcResult(null)
      return
    }

    const propSpr = parseFloat(propSpreadPoints)
    const dynamicSpreadActive = useDynamicSpread && !isNaN(propSpr) && propSpr > 0 && brokerSpreadPoints !== null && brokerSpreadPoints > 0 && brokerPointSize !== null

    const timer = setTimeout(async () => {
      setCalcLoading(true)
      try {
        const response = await accountsService.calculateTrading({
          provider: selectedProvider,
          prop_equity: equity,
          ixen_pair_id: parseInt(selectedPairId),
          phase: mappedPhase,
          direction,
          entry_price: entry,
          sl_percent: effectiveSlPercent,
          tp_percent: tpPercent,
          use_dynamic_spread: !!dynamicSpreadActive,
          prop_spread_points: dynamicSpreadActive ? propSpr : undefined,
          broker_spread_points: dynamicSpreadActive ? brokerSpreadPoints! : undefined,
          point_size: dynamicSpreadActive ? brokerPointSize! : undefined,
          buffer_usd: 0.15,
          funder_pro_margin_percent: selectedProvider === 'FunderPro' ? funderProMarginDecimal : undefined,
          fundednext_leverage: selectedProvider === 'FundedNext' ? fundedNextLeverage : undefined,
          fp2_risk_profile: isFp2 ? fp2RiskProfile : undefined,
          gold_price: goldPrice ?? undefined,
          broker_equity: brokerEquity ?? undefined,
          broker_margin_free: brokerMarginFree ?? undefined,
        })
        setCalcResult(response)
      } catch (err: any) {
        setCalcResult(null)
        console.error('IxenConsole calc error:', err)
      } finally {
        setCalcLoading(false)
      }
    }, 600)

    return () => clearTimeout(timer)
  }, [entryPrice, propEquity, selectedProvider, selectedPhase, direction, selectedPairId, effectiveSlPercent, tpPercent, useDynamicSpread, propSpreadPoints, brokerSpreadPoints, brokerPointSize, funderProMarginDecimal, fundedNextLeverage, fp2RiskProfile, isFp2])

  // ─── Data loading
  useEffect(() => {
    if (!canAccess) return
    const load = async () => {
      try {
        const [prov, pairsResp, access] = await Promise.all([
          accountsService.getPropProviders(),
          ixenSystemService.getPairs(),
          accountsService.getProviderAccess(),
        ])
        setProviders(prov)
        setIxenPairs((pairsResp.pairs ?? []).filter(p => p.is_active))
        setProviderAccessList(access.providers ?? access)
      } catch { toast.error('Errore nel caricamento dei dati') }
    }
    load()
  }, [canAccess])

  useEffect(() => {
    if (!canAccess || !selectedPairId) {
      setGoldPrice(null); setBrokerEquity(null); setBrokerMarginFree(null)
      setBrokerSpreadPoints(null); setBrokerPointSize(null); setBrokerSpreadError(null)
      setEaStatus(null); setEaAgeSec(null); setEaErrorMsg(null)
      return
    }
    let cancelled = false
    const load = async () => {
      setLoadingGoldPrice(true)
      if (useDynamicSpread) setLoadingBrokerSpread(true)
      setBrokerSpreadError(null)
      try {
        const r = await accountsService.getIxenPairInfo(parseInt(selectedPairId))
        if (cancelled) return
        setGoldPrice(r.gold_price)
        setBrokerEquity(r.broker_equity ?? null)
        setBrokerMarginFree(r.broker_margin_free ?? null)
        setEaStatus(r.ea_status ?? null)
        setEaAgeSec(r.last_seen_age_sec ?? null)
        setEaErrorMsg(null)
        if (useDynamicSpread) {
          setBrokerSpreadPoints(r.spread_points)
          setBrokerPointSize(r.point_size)
        }
      } catch (err: any) {
        if (cancelled) return
        setGoldPrice(null); setBrokerEquity(null); setBrokerMarginFree(null)
        setEaStatus('offline'); setEaAgeSec(null)
        const detail = err?.response?.data?.detail || err?.message || 'EA broker non disponibile'
        setEaErrorMsg(typeof detail === 'string' ? detail : 'EA broker non disponibile')
        if (useDynamicSpread) {
          setBrokerSpreadError('Impossibile leggere spread broker')
          setBrokerSpreadPoints(null); setBrokerPointSize(null)
        }
      } finally {
        if (!cancelled) {
          setLoadingGoldPrice(false)
          setLoadingBrokerSpread(false)
        }
      }
    }
    load()
    // Polling 5s allineato a telemetry EA
    const interval = setInterval(load, 5000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [selectedPairId, useDynamicSpread, canAccess])

  // ─── Phase options
  const phaseOptions = useMemo(() => {
    if (selectedProvider === FP2_PROVIDER_NAME) return [['ONE_STEP', 'One Step'], ['TWO_STEP', 'Two Step'], ['MASTER', 'Master']]
    if (selectedProvider === 'FundingPips') return [['CHALLENGE', 'Challenge'], ['REAL', 'Real']]
    if (selectedProvider === 'FunderPro') return [['CHALLENGE', 'Challenge'], ['REAL_PRE_PAYOUT', 'Pre 1° Payout'], ['REAL_POST_PAYOUT', 'Post 1° Payout']]
    const opts: [string, string][] = [['CHALLENGE', 'Challenge'], ['REAL_PRE_PAYOUT', 'Pre 3° Payout']]
    if (!isRestrictedUser) opts.push(['REAL_POST_PAYOUT', 'Post 3° Payout'])
    return opts
  }, [selectedProvider, isRestrictedUser])

  const phaseBadge = selectedPhase
    ? (selectedProvider === 'FunderPro' ? FUNDERPRO_PHASE_LABELS : PHASE_LABELS)[selectedPhase] ?? selectedPhase
    : '—'

  // ─── Reset on provider change
  const handleProviderChange = (value: string) => {
    const access = providerAccessList.find(p => p.provider_name === value)
    if (access && !access.has_access) { setSelectedLockedProvider(access); setPurchaseModalOpen(true); return }
    setSelectedProvider(value); setSelectedPropSize(''); setPropEquity(''); setSelectedPhase('')
    setDirection(''); setEntryPrice(''); setFunderProMarginMode('massimale'); setFp2RiskProfile('conservativo')
  }

  // ─── Slider helpers
  const slPct = effectiveSlPercent * 100
  const tpPct = tpPercent * 100
  const slFillPct = ((effectiveSlPercent - 0.002) / Math.max(slLimits.maxSlPercent - 0.002, 0.001)) * 100
  const TP_MIN = 0.0001
  const tpFillPct = ((tpPercent - TP_MIN) / Math.max(tpEffectiveMax - TP_MIN, 0.0001)) * 100

  return (
    <SubscriptionBlocker>
      <div className="p-4 min-h-screen" style={{ background: '#0a0f1e', color: '#e2e8f0' }}>

        {/* ── Two column grid */}
        <div className="grid grid-cols-2 gap-4 max-w-6xl mx-auto items-start">

          {/* ══ LEFT COLUMN ══════════════════════════════════════════════ */}
          <div>

            {/* Contesto Operativo */}
            <div className="bg-[#0d1529] border border-[#1e3a5f] rounded-xl p-4 mb-4">
              <div className="text-[11px] font-bold tracking-widest uppercase text-[#64748b] mb-2 flex items-center gap-2 before:content-['•'] before:text-[#38bdf8] before:text-base">Contesto Operativo</div>
              <div className="grid grid-cols-2 gap-2">

                {/* Provider */}
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] text-[#64748b] uppercase tracking-wider">Provider PROP</span>
                  <Select value={selectedProvider} onValueChange={handleProviderChange}>
                    <SelectTrigger className="h-9 bg-[#131d35] border-[#1e3a5f] text-sm font-semibold">
                      <SelectValue placeholder="Seleziona provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map(p => {
                        const acc = providerAccessList.find(a => a.provider_name === p.name)
                        const locked = acc && !acc.has_access
                        return (
                          <SelectItem key={p.id} value={p.name}>
                            <span className="flex items-center gap-2">
                              {p.name}
                              {locked && <span className="text-amber-400 text-[10px]">🔒 €{acc?.one_time_price}</span>}
                            </span>
                          </SelectItem>
                        )
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Broker */}
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] text-[#64748b] uppercase tracking-wider">Coppia iXen</span>
                  <Select value={selectedPairId} onValueChange={setSelectedPairId} disabled={!selectedProvider}>
                    <SelectTrigger className="h-9 bg-[#131d35] border-[#1e3a5f] text-sm font-semibold">
                      <SelectValue placeholder="Seleziona coppia" />
                    </SelectTrigger>
                    <SelectContent>
                      {ixenPairs.length === 0
                        ? <div className="px-2 py-2 text-xs text-[#64748b]">Nessuna coppia iXen attiva. Creane una in Conti.</div>
                        : ixenPairs.map(p => (
                            <SelectItem key={p.id} value={p.id.toString()}>
                              {p.display_name || `Coppia #${p.id}`} <span className="text-[10px] text-[#64748b] ml-1">({p.broker_login})</span>
                            </SelectItem>
                          ))}
                    </SelectContent>
                  </Select>
                  {/* EA status banner (Fase B) */}
                  {selectedPairId && eaStatus && (
                    <div className={
                      eaStatus === 'live'
                        ? 'mt-1 flex items-center gap-1.5 text-[10px] text-[#4ade80]'
                        : eaStatus === 'stale'
                          ? 'mt-1 flex items-center gap-1.5 text-[10px] text-[#fbbf24] bg-[#3a2a0a] border border-[#fbbf24]/40 rounded px-2 py-1'
                          : 'mt-1 flex items-center gap-1.5 text-[10px] text-[#f87171] bg-[#3a0a0a] border border-[#f87171]/40 rounded px-2 py-1'
                    }>
                      <span className={
                        eaStatus === 'live' ? 'inline-block w-1.5 h-1.5 rounded-full bg-[#4ade80] animate-pulse'
                          : eaStatus === 'stale' ? 'inline-block w-1.5 h-1.5 rounded-full bg-[#fbbf24]'
                          : 'inline-block w-1.5 h-1.5 rounded-full bg-[#f87171]'
                      }></span>
                      {eaStatus === 'live' && <span>EA broker connesso{eaAgeSec !== null ? ` · ${Math.round(eaAgeSec)}s fa` : ''}</span>}
                      {eaStatus === 'stale' && <span>EA broker in ritardo {eaAgeSec !== null ? `(${Math.round(eaAgeSec)}s fa)` : ''} — dati potrebbero essere obsoleti</span>}
                      {eaStatus === 'offline' && <span>{eaErrorMsg || 'EA broker non collegato. Avvia iXen_Bridge su MT4.'}</span>}
                    </div>
                  )}
                </div>

                {/* Fase */}
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] text-[#64748b] uppercase tracking-wider">Fase di Trading</span>
                  <Select value={selectedPhase} onValueChange={setSelectedPhase} disabled={!selectedPairId}>
                    <SelectTrigger className="h-9 bg-[#131d35] border-[#1e3a5f] text-sm font-semibold">
                      <SelectValue placeholder="Seleziona fase" />
                    </SelectTrigger>
                    <SelectContent>
                      {phaseOptions.map(([val, label]) => <SelectItem key={val} value={val}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Capitale */}
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] text-[#64748b] uppercase tracking-wider">Capitale PROP</span>
                  <Select value={selectedPropSize} onValueChange={v => { setSelectedPropSize(v); setPropEquity('') }} disabled={!selectedProvider}>
                    <SelectTrigger className="h-9 bg-[#131d35] border-[#1e3a5f] text-sm font-semibold">
                      <SelectValue placeholder="Seleziona capitale" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.find(p => p.name === selectedProvider)?.sizes?.filter(s => s.is_active).sort((a, b) => a.size - b.size).map(s => (
                        <SelectItem key={s.id} value={s.size.toString()}>{s.size >= 1000 ? `${s.size / 1000}K` : s.size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* FundedNext Leverage (solo Challenge) */}
                {selectedProvider === 'FundedNext' && selectedPhase === 'CHALLENGE' && (
                  <div className="col-span-2 flex flex-col gap-1">
                    <span className="text-[10px] text-[#64748b] uppercase tracking-wider">Leva FundedNext</span>
                    <Select value={String(fundedNextLeverage)} onValueChange={v => setFundedNextLeverage(parseInt(v) as 10 | 15)}>
                      <SelectTrigger className="h-9 bg-[#131d35] border-[#1e3a5f] text-sm font-semibold">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">🔴 Massimale — Leva 1:15</SelectItem>
                        <SelectItem value="10">🟢 Prudente — Leva 1:10</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-[#64748b]">
                      Leva attiva: <span className="text-[#38bdf8] font-bold">1:{fundedNextLeverage}</span> — usata solo in Challenge. In Real sempre 1:10.
                    </p>
                  </div>
                )}

                {/* FP2 Profilo Rischio (tutte le fasi — coperture diverse in One/Two Step) */}
                {isFp2 && (
                  <div className="col-span-2 flex flex-col gap-1">
                    <span className="text-[10px] text-[#64748b] uppercase tracking-wider">Profilo Rischio FP2</span>
                    <Select value={fp2RiskProfile} onValueChange={v => setFp2RiskProfile(v as 'conservativo' | 'aggressivo')} disabled={fp2ProfileLocked}>
                      <SelectTrigger className="h-9 bg-[#131d35] border-[#1e3a5f] text-sm font-semibold">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conservativo">🟢 Conservativo — copertura 17% / 34%</SelectItem>
                        <SelectItem value="aggressivo">🔴 Aggressivo — copertura 20% / 40%</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-[#64748b]">
                      Copertura broker: <span className="text-[#38bdf8] font-bold">
                        {selectedPhase === 'MASTER' ? '70% (fissa in Master)' : selectedPhase === 'TWO_STEP' ? (fp2RiskProfile === 'aggressivo' ? '40%' : '34%') : (fp2RiskProfile === 'aggressivo' ? '20%' : '17%')}
                      </span>{fp2ProfileLocked ? ' — 🔒 profilo fissato alla creazione della coppia (immutabile).' : ' — il profilo della coppia reale è fissato alla creazione.'}
                    </p>
                  </div>
                )}

                {/* FunderPro Margin Mode (solo Challenge) */}
                {selectedProvider === 'FunderPro' && selectedPhase === 'CHALLENGE' && (
                  <div className="col-span-2 flex flex-col gap-1">
                    <span className="text-[10px] text-[#64748b] uppercase tracking-wider">Modalità Margine</span>
                    <Select value={funderProMarginMode} onValueChange={v => setFunderProMarginMode(v as typeof funderProMarginMode)}>
                      <SelectTrigger className="h-9 bg-[#131d35] border-[#1e3a5f] text-sm font-semibold">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="prudente">🟢 Prudente — 65% margine</SelectItem>
                        <SelectItem value="standard">🟡 Standard — 80% margine</SelectItem>
                        <SelectItem value="massimale">🔴 Massimale — 95% margine</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-[#64748b]">
                      Margine utilizzato: <span className="text-[#38bdf8] font-bold">{Math.round(funderProMarginDecimal * 100)}%</span> del capitale prop
                    </p>
                  </div>
                )}

                {/* Equity */}
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] text-[#64748b] uppercase tracking-wider">Equity PROP ($)</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder={selectedPropSize ? parseInt(selectedPropSize).toLocaleString('it-IT') : '98.450'}
                    value={propEquity ? parseFloat(propEquity).toLocaleString('it-IT') : ''}
                    onChange={e => { const raw = e.target.value.replace(/\./g, '').replace(',', '.'); if (raw === '' || !isNaN(parseFloat(raw))) setPropEquity(raw) }}
                    disabled={!selectedPropSize}
                    className={`h-9 px-3 rounded-lg text-sm font-semibold bg-[#131d35] border text-[#38bdf8] outline-none transition-colors ${!equityValidation.isValid ? 'border-red-500' : 'border-[#1e3a5f] focus:border-[#2563eb]'}`}
                  />
                  {!equityValidation.isValid && <p className="text-[10px] text-red-400">{equityValidation.message}</p>}
                </div>

                {/* Fase badge */}
                <div className="flex flex-col gap-1 justify-end">
                  <span className="text-[10px] text-[#64748b] uppercase tracking-wider">Fase Attiva</span>
                  <div className="flex items-center h-9">
                    <span className="bg-[#1e3a5f] text-[#38bdf8] px-3 py-1 rounded-full text-xs font-bold tracking-wider border border-[#2d5a8e]">
                      {phaseBadge.toUpperCase()}
                    </span>
                  </div>
                </div>

              </div>
            </div>

            {/* LOT ENGINE */}
            <div className={card}>
              <div className={cardTitle}>Lot Engine</div>
              {lotsCalc ? (
                <>
                  <div className="grid grid-cols-3 items-center gap-2 mb-4">
                    <div className="bg-[#131d35] border border-[#1e3a5f] rounded-lg p-3 text-center">
                      <div className="text-[9px] text-[#64748b] uppercase tracking-wider mb-1">Lot PROP</div>
                      <div className="text-2xl font-black text-[#f87171] tabular-nums">{lotsCalc.lotProp.toFixed(2)}</div>
                      <div className="text-[11px] font-bold text-[#f87171] mt-1">● {direction || '—'}</div>
                    </div>
                    <div className="flex justify-center text-[#38bdf8] text-xl">⟹</div>
                    <div className="bg-[#131d35] border border-[#1e3a5f] rounded-lg p-3 text-center">
                      <div className="text-[9px] text-[#64748b] uppercase tracking-wider mb-1">Lot BROKER</div>
                      <div className="text-2xl font-black text-[#4ade80] tabular-nums">{lotsCalc.lotBroker.toFixed(2)}</div>
                      <div className="text-[11px] font-bold text-[#4ade80] mt-1">● {direction ? (direction === 'BUY' ? 'SELL' : 'BUY') : '—'}</div>
                    </div>
                  </div>
                  <div className="text-[10px] text-[#94a3b8] text-center mb-2">Copertura: {lotsCalc.coveragePercent}%</div>
                  <div className="h-1 rounded-full overflow-hidden" style={{ background: '#1e3a5f' }}>
                    <div className="h-full rounded-full" style={{ width: `${lotsCalc.coveragePercent}%`, background: 'linear-gradient(90deg,#38bdf8,#818cf8)' }} />
                  </div>
                </>
              ) : (
                <div className="text-center text-[#475569] text-sm py-6">
                  {loadingGoldPrice ? 'Caricamento prezzo oro...' : !selectedPairId ? 'Seleziona la coppia iXen per calcolare i lotti' : !goldPrice ? 'Prezzo oro non disponibile' : 'Seleziona equity e fase per calcolare'}
                </div>
              )}
              {goldPrice && (
                <div className="mt-3 pt-3 border-t border-[#1e3a5f] flex justify-between text-[10px] text-[#64748b]">
                  <span>XAU/USD</span>
                  <span className="text-[#f59e0b] font-bold">${goldPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
            </div>

            {/* HEDGE STATUS */}
            <div className={card}>
              <div className={cardTitle}>Stato Hedge</div>
              {lotsCalc ? (
                <>
                  <div className={`rounded-lg p-2.5 flex items-center gap-2.5 mb-2.5 ${lotsCalc.brokerCanTrade ? 'bg-[#0a1f0f] border border-[#166534]' : 'bg-[#1c0a0a] border border-[#7f1d1d]'}`}>
                    <span className="text-lg">{lotsCalc.brokerCanTrade ? '✅' : '❌'}</span>
                    <div>
                      <div className={`text-xs font-black tracking-wider ${lotsCalc.brokerCanTrade ? 'text-[#4ade80]' : 'text-[#f87171]'}`}>
                        {lotsCalc.brokerCanTrade ? 'HEDGE ESEGUIBILE' : 'MARGINE INSUFFICIENTE'}
                      </div>
                      <div className={`text-[10px] mt-0.5 ${lotsCalc.brokerCanTrade ? 'text-[#86efac]' : 'text-[#fca5a5]'}`}>
                        {lotsCalc.brokerCanTrade ? 'Disponibilità broker sufficiente' : `Necessari almeno ${fmtUsd(lotsCalc.brokerMinRequired)}`}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      ['Margine Richiesto', fmtUsd(lotsCalc.brokerMarginOpening)],
                      ['Equity Disponibile', lotsCalc.brokerEquity !== null ? fmtUsd(lotsCalc.brokerEquity!) : '—'],
                      ['Min. Consigliato', fmtUsd(lotsCalc.brokerMinRequired)],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <div className="text-[9px] text-[#64748b] uppercase tracking-wider mb-0.5">{label}</div>
                        <div className={`text-xs font-bold ${label === 'Equity Disponibile' && lotsCalc.brokerCanTrade ? 'text-[#4ade80]' : 'text-[#e2e8f0]'}`}>{value}</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center text-[#475569] text-sm py-4">In attesa di dati...</div>
              )}
            </div>

          </div>

          {/* ══ RIGHT COLUMN ══════════════════════════════════════════════ */}
          <div>
            <div className={`${card} !mb-0`}>
              <div className={cardTitle}>Conferma Posizione</div>

              {/* Direction */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                {(['BUY', 'SELL'] as const).map(dir => (
                  <button
                    key={dir}
                    onClick={() => setDirection(dir)}
                    disabled={!selectedPhase}
                    className={`py-2.5 rounded-xl text-base font-black tracking-wider border-2 transition-all ${
                      !selectedPhase ? 'opacity-40 cursor-not-allowed bg-gray-800 border-gray-700 text-gray-600' :
                      direction === dir
                        ? dir === 'BUY'
                          ? 'bg-[#14532d] border-[#4ade80] text-[#4ade80] shadow-[0_0_16px_rgba(74,222,128,0.2)]'
                          : 'bg-[#450a0a] border-[#f87171] text-[#f87171] shadow-[0_0_16px_rgba(248,113,113,0.2)]'
                        : dir === 'BUY'
                          ? 'bg-[#052e16] border-[#166534] text-[#4ade80] hover:bg-[#0a3d1c]'
                          : 'bg-[#1c0a0a] border-[#7f1d1d] text-[#f87171] hover:bg-[#2d0f0f]'
                    }`}
                  >
                    {dir === 'BUY' ? '↑' : '↓'} {dir}
                  </button>
                ))}
              </div>

              {/* Price input */}
              <div className="mb-2">
                <span className="text-[10px] text-[#64748b] uppercase tracking-wider">Prezzo di Entrata (PROP)</span>
              </div>
              <input
                type="number"
                step="0.01"
                placeholder="es. 3125.50"
                value={entryPrice}
                onChange={e => setEntryPrice(e.target.value)}
                disabled={!direction}
                className="w-full mb-2 px-4 py-2.5 rounded-xl text-2xl font-black text-[#38bdf8] bg-[#131d35] border-2 border-[#2563eb] outline-none focus:border-[#60a5fa] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.15)] tabular-nums transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              />
              {entryPrice && direction && (
                <div className="flex items-center gap-2 text-xs text-[#4ade80] mb-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#4ade80] animate-pulse" />
                  Calcolo automatico attivo — si aggiorna in tempo reale
                </div>
              )}

              {/* Spread Dinamico */}
              <div className="bg-[#131d35] border border-[#1e3a5f] rounded-xl p-3 mb-3">
                <div className="flex justify-between items-center">
                  <span className="text-[11px] font-bold text-[#94a3b8] uppercase tracking-wider">Spread Dinamico</span>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={useDynamicSpread}
                      onCheckedChange={setUseDynamicSpread}
                      disabled={!selectedPairId}
                    />
                    <span className="text-[11px] text-[#64748b]">
                      {useDynamicSpread ? 'ON — calcolo dinamico' : 'OFF — offset statico 0.30'}
                    </span>
                  </div>
                </div>
                {useDynamicSpread && (
                  <div className="mt-3 grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-[10px] text-[#64748b] mb-1">Spread PROP (pts)</div>
                      <input
                        type="number"
                        step="1"
                        placeholder="es. 12"
                        value={propSpreadPoints}
                        onChange={e => setPropSpreadPoints(e.target.value)}
                        className="w-full h-8 px-2 rounded-md text-sm bg-[#0d1529] border border-[#1e3a5f] text-[#e2e8f0] outline-none focus:border-[#2563eb]"
                      />
                    </div>
                    <div>
                      <div className="text-[10px] text-[#64748b] mb-1">Spread BROKER</div>
                      <div className="h-8 px-2 rounded-md text-sm bg-[#0d1529] border border-[#1e3a5f] flex items-center gap-1 text-[#38bdf8] font-semibold">
                        {loadingBrokerSpread ? <span className="text-[#64748b] text-xs">Lettura...</span>
                          : brokerSpreadError ? <span className="text-red-400 text-xs">{brokerSpreadError}</span>
                          : brokerSpreadPoints !== null ? <>{brokerSpreadPoints} pts <span className="text-[9px] bg-[#1e3a5f] text-[#38bdf8] px-1 rounded">AUTO</span></>
                          : <span className="text-[#64748b] text-xs">—</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-[#64748b] mb-1">Offset Calcolato</div>
                      <div className="h-8 px-2 rounded-md text-sm bg-[#0d1529] border border-[#1e3a5f] flex items-center text-[#e2e8f0] font-semibold tabular-nums">
                        {calcResult?.offset_mode === 'dynamic' ? `$${calcResult.offset_price?.toFixed(4)}` : '—'}
                      </div>
                    </div>
                    {brokerPointSize && (
                      <div className="col-span-3 text-[10px] text-[#475569]">
                        pointSize: {brokerPointSize} · buffer: $0.15 (15 pts)
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* SL Slider */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2 px-3 py-2 rounded-lg bg-[#1c0a0a] border border-[#7f1d1d]">
                  <span className="text-xs font-black text-[#f87171] uppercase tracking-widest">⬇ Stop Loss</span>
                  <span className="text-xl font-black text-[#f87171] tabular-nums">{slPct.toFixed(2)}%</span>
                </div>
                <div className="relative">
                  <input
                    type="range"
                    min={0.002}
                    max={slLimits.maxSlPercent}
                    step={0.0001}
                    value={Math.max(effectiveSlPercent, 0.002)}
                    onChange={e => setSlPercent(parseFloat(e.target.value))}
                    disabled={!direction}
                    className="w-full h-1 appearance-none rounded-full cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      background: `linear-gradient(90deg, #f87171 ${slFillPct}%, #1e3a5f ${slFillPct}%)`,
                    }}
                  />
                </div>
                <div className="flex justify-between mt-1.5 text-[10px] text-[#475569]">
                  <span>0.20%</span>
                  {slLimits.bufferPercent < slLimits.providerMax && propEquity && (
                    <span className="text-amber-400">Buffer: ${slLimits.bufferDollar.toFixed(0)} ({(slLimits.bufferPercent * 100).toFixed(1)}%)</span>
                  )}
                  <span className={slLimits.bufferPercent < slLimits.providerMax ? 'text-amber-400' : ''}>
                    {(slLimits.maxSlPercent * 100).toFixed(1)}%{slLimits.bufferPercent < slLimits.providerMax ? ' (limitato)' : ''}
                  </span>
                </div>
              </div>

              {/* TP Slider */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2 px-3 py-2 rounded-lg bg-[#0a1f0f] border border-[#166534]">
                  <span className="text-xs font-black text-[#4ade80] uppercase tracking-widest">⬆ Take Profit</span>
                  <span className="text-xl font-black text-[#4ade80] tabular-nums">{tpPct < 0.1 ? tpPct.toFixed(3) : tpPct.toFixed(2)}%</span>
                </div>
                <input
                  type="range"
                  min={TP_MIN}
                  max={tpEffectiveMax}
                  step={0.0001}
                  value={Math.min(Math.max(tpPercent, TP_MIN), tpEffectiveMax)}
                  onChange={e => setTpPercent(parseFloat(e.target.value))}
                  disabled={!selectedPhase}
                  className="w-full h-1 appearance-none rounded-full cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: `linear-gradient(90deg, #4ade80 ${tpFillPct}%, #1e3a5f ${tpFillPct}%)`,
                  }}
                />
                <div className="flex justify-between mt-1.5 text-[10px] text-[#475569]">
                  <span>{(TP_MIN * 100).toFixed(2)}%</span>
                  <span>
                    Max: <span className="bg-[#0f2340] text-[#38bdf8] rounded px-1 py-0.5 font-bold">
                      {(tpEffectiveMax * 100).toFixed(2)}%
                      {selectedProvider && selectedPhase ? ` — ${selectedProvider}` : ''}
                    </span>
                  </span>
                  <span>{(tpEffectiveMax * 100).toFixed(2)}%</span>
                </div>
              </div>

              {/* Results */}
              {calcLoading ? (
                <div className="rounded-xl border border-[#1e3a5f] bg-[#131d35] p-6 text-center text-[#475569] text-sm animate-pulse">
                  Calcolo in corso...
                </div>
              ) : calcResult ? (
                <div className="grid grid-cols-2 gap-3">
                  {/* PROP panel */}
                  <div className="bg-[#131d35] rounded-xl p-4 border border-[#1e3a5f] border-t-2 border-t-[#f87171]">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-[11px] font-bold tracking-wider text-[#f87171]">PROP</span>
                      <span className="text-[11px] text-[#64748b]">{calcResult.prop_direction} {calcResult.lot_prop.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[#1e3a5f]">
                      <span className="text-xs font-black text-[#f87171] uppercase tracking-widest bg-[#3b0a0a] px-2 py-0.5 rounded">SL</span>
                      <span className="text-base font-black tabular-nums text-[#e2e8f0]">
                        {fmtUsd(calcResult.prop_sl)}
                        <span className="text-[10px] font-normal ml-1 text-[#f87171] opacity-70">{fmtDelta(-(calcResult.prop_size_detected * effectiveSlPercent))}</span>
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-xs font-black text-[#4ade80] uppercase tracking-widest bg-[#052e16] px-2 py-0.5 rounded">TP</span>
                      <span className="text-base font-black tabular-nums text-[#e2e8f0]">
                        {fmtUsd(calcResult.prop_tp)}
                        <span className="text-[10px] font-normal ml-1 text-[#4ade80] opacity-70">{fmtDelta(Math.abs(calcResult.prop_tp - parseFloat(entryPrice)) * calcResult.lot_prop * 100)}</span>
                      </span>
                    </div>
                  </div>

                  {/* BROKER panel */}
                  <div className="bg-[#131d35] rounded-xl p-4 border border-[#1e3a5f] border-t-2 border-t-[#4ade80]">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-[11px] font-bold tracking-wider text-[#4ade80]">BROKER</span>
                      <span className="text-[11px] text-[#64748b]">{calcResult.broker_direction} {calcResult.lot_broker.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[#1e3a5f]">
                      <span className="text-xs font-black text-[#f87171] uppercase tracking-widest bg-[#3b0a0a] px-2 py-0.5 rounded">SL</span>
                      <span className="text-base font-black tabular-nums text-[#e2e8f0]">{fmtUsd(calcResult.broker_sl)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-xs font-black text-[#4ade80] uppercase tracking-widest bg-[#052e16] px-2 py-0.5 rounded">TP</span>
                      <span className="text-base font-black tabular-nums text-[#e2e8f0]">{fmtUsd(calcResult.broker_tp)}</span>
                    </div>
                    {calcResult.warnings && calcResult.warnings.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-[#1e3a5f] space-y-1">
                        {calcResult.warnings.map((w, i) => (
                          <div key={i} className="text-[10px] text-amber-400">⚠ {w}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-[#1e3a5f] bg-[#131d35] p-6 text-center text-[#475569] text-sm">
                  Inserisci il prezzo di entrata per visualizzare SL e TP
                </div>
              )}

            </div>
          </div>

        </div>

        {/* Provider purchase modal */}
        {purchaseModalOpen && selectedLockedProvider && (
          <ProviderPurchaseModal
            provider={selectedLockedProvider}
            onClose={() => { setPurchaseModalOpen(false); setSelectedLockedProvider(null) }}
            onSuccess={async () => {
              try {
                const access = await accountsService.getProviderAccess()
                setProviderAccessList(access.providers ?? access)
              } catch {}
              setPurchaseModalOpen(false)
              setSelectedLockedProvider(null)
            }}
          />
        )}

      </div>
    </SubscriptionBlocker>
  )
}
